import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '@/hooks/useSubscription';
import { useStripePortal } from '@/hooks/useStripePortal';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export function SubscriptionManager() {
  const navigate = useNavigate();
  const { subscriptionDetails, error: subError, loading: subLoading } = useSubscription();
  const { redirectToPortal, loading: portalLoading, error: portalError } = useStripePortal();

  const getStatusBadgeVariant = useCallback((status: string | null) => {
    switch (status) {
      case 'trialing':
        return 'outline';
      case 'active':
        return 'default';
      case 'canceled':
        return 'destructive';
      default:
        return 'secondary';
    }
  }, []);

  const handleManageSubscription = async () => {
    if (subscriptionDetails) {
      await redirectToPortal();
    } else {
      navigate('/subscription');
    }
  };

  if (subError || portalError) {
    const error = subError || portalError;
    return (
      <Card className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            There was an error syncing with Stripe. Please try again later.
            <p className="text-sm mt-1 text-muted-foreground">{error?.message}</p>
          </AlertDescription>
        </Alert>
      </Card>
    );
  }

  if (subLoading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-6 w-24" />
          </div>
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </Card>
    );
  }

  if (!subscriptionDetails) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Subscription Status</h2>
            <Badge variant="secondary">No Subscription</Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            You currently don't have an active subscription. Choose a plan to get started.
          </p>
          <Button
            onClick={handleManageSubscription}
            className="w-full"
            disabled={portalLoading}
          >
            {portalLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Choose a Plan'
            )}
          </Button>
        </div>
      </Card>
    );
  }

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Subscription Status</h2>
          <Badge variant={getStatusBadgeVariant(subscriptionDetails.status)}>
            {subscriptionDetails.status}
          </Badge>
        </div>

        <div className="text-sm space-y-2">
          <p className="font-medium">Plan Type: {subscriptionDetails.plan}</p>
          
          {subscriptionDetails.status === 'trialing' && (
            <>
              <p>Trial Started: {formatDate(subscriptionDetails.trial_start)}</p>
              <p>Trial Ends: {formatDate(subscriptionDetails.trial_end)}</p>
              <p className="text-yellow-600 dark:text-yellow-400">
                Your trial will end on {formatDate(subscriptionDetails.trial_end)}
              </p>
            </>
          )}

          {subscriptionDetails.current_period_end && (
            <p className="text-muted-foreground">
              Current billing period ends: {formatDate(subscriptionDetails.current_period_end)}
            </p>
          )}

          {subscriptionDetails.cancel_at_period_end && (
            <p className="text-destructive mt-2">
              Your subscription will be canceled at the end of the current period
            </p>
          )}
        </div>

        <Button
          onClick={handleManageSubscription}
          variant="outline"
          className="w-full mt-4"
          disabled={portalLoading}
        >
          {portalLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading...
            </>
          ) : (
            'Manage Subscription'
          )}
        </Button>
      </div>
    </Card>
  );
}
